import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ChevronLeft, Clock, CheckCircle2, Send, Loader2, Shield, User, StickyNote, Paperclip, Download } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import NavBar from "@/components/NavBar";
import SupportAttachmentPicker from "@/components/support/SupportAttachmentPicker";

/**
 * iter72j · Ticket Detail — shared page for both submitters and SAs.
 *
 * Route: `/support/tickets/:ticketId` (submitter)
 *        `/super-admin/support/:ticketId` (SA, same page — SA sees extra
 *         controls: status/priority/assign + internal notes).
 *
 * SA-specific controls are gated via the current user role.
 */

const PRIORITIES = ["low", "normal", "high", "urgent"];
const STATUSES = ["new", "in_progress", "waiting", "resolved"];

export default function SupportTicketDetailPage({ isSuperAdminView = false }) {
  const { ticketId } = useParams();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [events, setEvents] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [reply, setReply] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [isInternal, setIsInternal] = useState(false);
  const [replyAtts, setReplyAtts] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ticketR, meR] = await Promise.all([
        api.get(`/support/tickets/${ticketId}`),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setTicket(ticketR.data.ticket);
      setEvents(ticketR.data.events || []);
      setMe(meR.data);
      if (isSuperAdminView) {
        try {
          const { data } = await api.get("/super-admin/support/assignees");
          setAssignees(data.assignees || []);
        } catch (_) { /* non-fatal */ }
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [ticketId, isSuperAdminView]);

  useEffect(() => { load(); }, [load]);

  const postReply = async () => {
    setReplyBusy(true); setErr("");
    try {
      await api.post(`/support/tickets/${ticketId}/messages`, {
        body: reply.trim(),
        is_internal: isInternal,
        attachment_ids: replyAtts.map((a) => a.attachment_id),
      });
      setReply(""); setIsInternal(false); setReplyAtts([]);
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setReplyBusy(false); }
  };

  const updateStatus = async (status) => {
    if (!window.confirm(`Change status to "${status}"?`)) return;
    try {
      await api.post(`/super-admin/support/tickets/${ticketId}/status`, { status });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const updatePriority = async (priority) => {
    try {
      await api.post(`/super-admin/support/tickets/${ticketId}/priority`, { priority });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const updateAssignee = async (assigned_to) => {
    try {
      await api.post(`/super-admin/support/tickets/${ticketId}/assign`, {
        assigned_to: assigned_to || null,
      });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const isSA = me?.role === "super_admin";
  const backHref = useMemo(() => (isSA ? "/super-admin/support" : "/support/tickets"), [isSA]);

  return (
    <>
      <NavBar />
      <main className="max-w-5xl mx-auto px-5 sm:px-10 py-10" data-testid="support-ticket-detail">
        <a href={backHref}
           className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#7A7A7A] hover:text-[#1A422D]">
          <ChevronLeft size={14} /> Back
        </a>

        {loading && <div className="mt-8 text-sm text-[#7A7A7A]">Loading…</div>}
        {err && (
          <div className="mt-6 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-xs flex items-center gap-2">
            <AlertTriangle size={12} /> {err}
          </div>
        )}
        {ticket && (
          <>
            <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#7A7A7A] font-mono">{ticket.ticket_id}</div>
                <h1 className="font-serif text-3xl text-[#1A422D] mt-1">{ticket.subject}</h1>
                <div className="mt-2 text-xs text-[#4A4A4A]">
                  From <strong>{ticket.submitter?.name}</strong> ({ticket.submitter?.role}) · {ticket.submitter?.email}
                </div>
              </div>
              <StatusBadge status={ticket.status} />
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-5">
                <div className="border border-[#E5E3D8] bg-white p-5">
                  <div className="overline text-[#B59A5A] mb-2">Original request</div>
                  <p className="whitespace-pre-wrap text-sm text-[#1A1A1A]">{ticket.body}</p>
                  <AttachmentList attachments={ticket.attachments} />
                </div>

                <div className="space-y-3" data-testid="ticket-events">
                  {events.map((ev) => <EventCard key={ev.event_id} event={ev} />)}
                </div>

                <div className="border border-[#1A422D] bg-white p-5" data-testid="ticket-reply-box">
                  <div className="overline text-[#B59A5A] mb-2">
                    {isSA ? "Reply or add an internal note" : "Reply"}
                  </div>
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={4}
                    data-testid="ticket-reply"
                    className="input-field w-full text-sm"
                    placeholder={isSA ? "Type a reply the submitter will see, or check 'Internal note' below…" : "Type a reply…"}
                  />
                  <div className="mt-3">
                    <SupportAttachmentPicker
                      attachments={replyAtts}
                      setAttachments={setReplyAtts}
                      testIdPrefix="ticket-reply-attach"
                    />
                  </div>
                  <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
                    {isSA ? (
                      <label className="inline-flex items-center gap-2 text-xs text-[#4A4A4A]">
                        <input type="checkbox" checked={isInternal}
                               onChange={(e) => setIsInternal(e.target.checked)}
                               data-testid="ticket-reply-internal"
                               className="h-4 w-4 accent-[#1A422D]" />
                        <StickyNote size={12} /> Internal note (submitter can&rsquo;t see this)
                      </label>
                    ) : <span />}
                    <button
                      type="button"
                      onClick={postReply}
                      disabled={replyBusy || !reply.trim()}
                      data-testid="ticket-reply-submit"
                      className="inline-flex items-center gap-2 bg-[#1A422D] text-[#F9F8F4] px-4 py-2 text-[11px] uppercase tracking-[0.22em] disabled:opacity-50 hover:bg-[#0f1a14]"
                    >
                      {replyBusy && <Loader2 size={12} className="animate-spin" />}
                      <Send size={12} /> {replyBusy ? "Posting…" : (isInternal ? "Post note" : "Send reply")}
                    </button>
                  </div>
                </div>
              </div>

              <aside className="space-y-5">
                <div className="border border-[#E5E3D8] bg-white p-5">
                  <div className="overline text-[#B59A5A]">SLA</div>
                  <SlaLine ticket={ticket} />
                  {ticket.escalated_count > 0 && (
                    <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-[#8a1a1a]">
                      Escalated {ticket.escalated_count}×
                    </div>
                  )}
                </div>

                {isSA && (
                  <>
                    <SACard title="Status">
                      <select value={ticket.status} onChange={(e) => updateStatus(e.target.value)}
                              data-testid="sa-ticket-status" className="input-field text-sm">
                        {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                      </select>
                    </SACard>
                    <SACard title="Priority">
                      <select value={ticket.priority} onChange={(e) => updatePriority(e.target.value)}
                              data-testid="sa-ticket-priority" className="input-field text-sm">
                        {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </SACard>
                    <SACard title="Assigned to">
                      <select value={ticket.assigned_to || ""}
                              onChange={(e) => updateAssignee(e.target.value)}
                              data-testid="sa-ticket-assign" className="input-field text-sm">
                        <option value="">Unassigned</option>
                        {assignees.map((a) => (
                          <option key={a.user_id} value={a.user_id}>{a.name}</option>
                        ))}
                      </select>
                    </SACard>
                  </>
                )}

                {!isSA && ticket.assigned_to_name && (
                  <div className="border border-[#E5E3D8] bg-white p-5">
                    <div className="overline text-[#B59A5A]">Handled by</div>
                    <div className="text-sm text-[#1A422D] mt-1 inline-flex items-center gap-2">
                      <Shield size={12} /> {ticket.assigned_to_name}
                    </div>
                  </div>
                )}
              </aside>
            </div>
          </>
        )}
      </main>
    </>
  );
}

function SACard({ title, children }) {
  return (
    <div className="border border-[#E5E3D8] bg-white p-5">
      <div className="overline text-[#B59A5A] mb-2">{title}</div>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = (status || "new").toLowerCase();
  const color = {
    new: "border-[#1A422D] text-[#1A422D] bg-[#E9F0EB]",
    in_progress: "border-[#B59A5A] text-[#8a6f33] bg-[#FBF6E8]",
    waiting: "border-[#7A7A7A] text-[#4A4A4A] bg-[#F3F1E9]",
    resolved: "border-[#1A422D] text-white bg-[#1A422D]",
  }[s] || "border-[#7A7A7A] text-[#4A4A4A]";
  return (
    <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.22em] border ${color}`}
          data-testid="ticket-status-badge">
      {s.replace("_", " ")}
    </span>
  );
}

function SlaLine({ ticket }) {
  if (ticket.status === "resolved") {
    return <div className="text-sm text-[#1A422D] inline-flex items-center gap-1 mt-1"><CheckCircle2 size={12} /> Resolved</div>;
  }
  if (ticket.first_responded_at) {
    return <div className="text-sm text-[#1A422D] inline-flex items-center gap-1 mt-1"><CheckCircle2 size={12} /> First response sent</div>;
  }
  const due = ticket.sla_due_at ? new Date(ticket.sla_due_at) : null;
  if (!due) return null;
  const diff = due.getTime() - Date.now();
  const hours = Math.floor(Math.abs(diff) / 36e5);
  const mins = Math.floor((Math.abs(diff) % 36e5) / 6e4);
  const pretty = `${hours}h ${mins}m`;
  if (diff < 0) return <div className="text-sm text-[#8a1a1a] inline-flex items-center gap-1 mt-1"><AlertTriangle size={12} /> Breached {pretty} ago</div>;
  return <div className="text-sm text-[#4A4A4A] inline-flex items-center gap-1 mt-1"><Clock size={12} /> Due in {pretty}</div>;
}

function EventCard({ event }) {
  const t = event.type;
  const isMsg = t === "message";
  const isNote = t === "internal_note";
  const isAudit = ["status_change", "priority_change", "assignment", "escalation"].includes(t);
  if (isAudit) {
    return (
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] px-2 py-1"
           data-testid={`ticket-event-${event.event_id}`}>
        {new Date(event.created_at).toLocaleString()} · {event.body}
      </div>
    );
  }
  const bg = isNote ? "bg-[#FBF6E8] border-[#B59A5A]" : "bg-white border-[#E5E3D8]";
  return (
    <div className={`border ${bg} p-4`} data-testid={`ticket-event-${event.event_id}`}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-2">
        <span className="inline-flex items-center gap-1">
          {event.author?.role === "super_admin" ? <Shield size={11} /> : <User size={11} />}
          {event.author?.name} · {event.author?.role}
        </span>
        <span>{new Date(event.created_at).toLocaleString()}</span>
      </div>
      {isNote && <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a6f33] mb-1">Internal note</div>}
      <p className="whitespace-pre-wrap text-sm text-[#1A1A1A]">{event.body}</p>
      <AttachmentList attachments={event.attachments} />
    </div>
  );
}

function AttachmentList({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return (
    <ul className="mt-3 space-y-1" data-testid="ticket-event-attachments">
      {attachments.map((a) => (
        <li key={a.attachment_id}
            className="flex items-center gap-2 border border-[#E5E3D8] bg-[#FBF9F1] px-2 py-1 text-xs">
          <Paperclip size={11} className="text-[#8a6f33]" />
          <a
            href={`${backend}/api/support/attachments/${a.attachment_id}`}
            target="_blank" rel="noopener noreferrer"
            className="flex-1 min-w-0 truncate text-[#1A422D] hover:underline inline-flex items-center gap-1"
          >
            <span className="truncate">{a.filename}</span>
            <Download size={10} className="opacity-60 shrink-0" />
          </a>
          <span className="text-[10px] text-[#7A7A7A] shrink-0">{_fmtSize(a.size)}</span>
        </li>
      ))}
    </ul>
  );
}

function _fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
